import { getApiConfig } from '@utils/getApiConfig';
import { isRateLimited } from '@utils/rateLimit';

/**
 * Outcome of a password-reset request, deliberately coarser than the API's
 * own answer — see TA-2592 (VAPT: user enumeration on forgot password).
 *
 * `submitted` is returned whether or not the address belongs to a registered
 * account. Every caller used to branch on the backend's `success` flag: the
 * login form showed an inline error, the confirmation screen alerted the
 * backend's message. Either one turned the public forgot-password form into
 * an oracle for "does this person have a TaskTag account?".
 *
 * The other three outcomes are each safe to surface, for the same reason:
 * none of them can vary with whether the account exists.
 */
export type PasswordResetResult =
  | 'submitted'
  | 'throttled'
  | 'invalid'
  | 'unavailable';

export const requestPasswordReset = async (
  email: string
): Promise<PasswordResetResult> => {
  const { apiUrl } = getApiConfig();

  try {
    const response = await fetch(`${apiUrl}/api/v1/password.json`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      // Mirrors the backend's own `to_s.downcase.strip`, so the address we
      // send is the one it will act on.
      body: JSON.stringify({
        user: { email: email.trim().toLowerCase() },
      }),
    });

    // TA-10679 keyed the rate-limit counter on the submitted address, so the
    // 429 now triggers on the same request number whether or not the address
    // has an account — it carries no information about existence, and hiding
    // it only costs real users, who would otherwise be told to check an inbox
    // no mail was ever sent to.
    //
    // This is exactly false against a backend WITHOUT that fix, where the
    // counter lived under a user id and so only ever existed for addresses
    // that have one: there, "too many attempts" is itself an enumeration
    // oracle. Do not ship this branch ahead of tasktag-backend#4344.
    if (isRateLimited(response)) return 'throttled';

    // 400 is the backend rejecting the address as malformed or over its
    // 254-octet cap. Whether a string parses as an address is decidable
    // client-side and says nothing about whether an account sits behind it,
    // so reporting it reopens nothing — and calling it "sent" would be a
    // plain lie. Reachable because our yup `.email()` and the backend's
    // `URI::MailTo::EMAIL_REGEXP` do not agree on every input.
    if (response.status === 400) return 'invalid';

    // Everything else the server says *about the address* collapses to one
    // outcome. Only its own failures (5xx) are reported.
    return response.status >= 500 ? 'unavailable' : 'submitted';
  } catch {
    // Transport failure (offline, DNS, Cloudflare edge). Says nothing about
    // the address, so it is safe — and honest — to surface.
    return 'unavailable';
  }
};
